/* eslint-disable no-await-in-loop */
/**
 * createThumbnail
 *
 * Generates a small downsized JPEG blob URL from a File for preview rendering.
 * The original File is NOT modified or consumed — callers keep using it for upload.
 *
 * Why this exists:
 *   Rendering 70+ full-resolution photo blob URLs in <img> tags forces the browser
 *   to decode each image into a full pixel buffer (e.g. 4032x3024 = ~48 MB of RGBA).
 *   On Safari this exhausts the per-tab memory budget and the page crashes.
 *   A 256x256 JPEG thumb is ~10–30 KB and decodes to ~256 KB.
 *
 * Strategy:
 *   1. createImageBitmap with resizeWidth/Height — fast, off-main-thread on Safari 15+/Chrome
 *   2. Fallback to <img> + <canvas> for older browsers / unsupported types (HEIC pre-conversion)
 *   3. If both fail (e.g. HEIC on a browser without heic2any decoded yet), return null;
 *      caller should fall back to the original blob URL.
 */

const DEFAULT_MAX_DIM = 320;
const DEFAULT_QUALITY = 0.7;

function canvasToBlobUrl(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/jpeg',
      quality,
    );
  });
}

async function viaCreateImageBitmap(file, maxDim, quality) {
  if (typeof createImageBitmap !== 'function') return null;

  let bitmap;
  try {
    // Probe natural size first so we preserve aspect ratio.
    const probe = await createImageBitmap(file);
    const ratio = Math.min(1, maxDim / Math.max(probe.width, probe.height));
    const targetW = Math.max(1, Math.round(probe.width * ratio));
    const targetH = Math.max(1, Math.round(probe.height * ratio));

    try {
      bitmap = await createImageBitmap(file, {
        resizeWidth: targetW,
        resizeHeight: targetH,
        resizeQuality: 'medium',
      });
    } catch (e) {
      // Some browsers reject the resize options object; fall back to drawing the probe.
      bitmap = probe;
    }
    if (bitmap !== probe && typeof probe.close === 'function') probe.close();
  } catch (e) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0);

  if (typeof bitmap.close === 'function') bitmap.close();

  const url = await canvasToBlobUrl(canvas, quality);
  // Release backing store (Safari is slow to GC large canvases).
  canvas.width = 0;
  canvas.height = 0;
  return url;
}

function viaImageElement(file, maxDim, quality) {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      try {
        const ratio = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
        const targetW = Math.max(1, Math.round(img.naturalWidth * ratio));
        const targetH = Math.max(1, Math.round(img.naturalHeight * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, targetW, targetH);

        const url = await canvasToBlobUrl(canvas, quality);
        canvas.width = 0;
        canvas.height = 0;
        URL.revokeObjectURL(objectUrl);
        resolve(url);
      } catch (e) {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });
}

/**
 * Create a downsized JPEG preview URL for a File.
 * Returns null on failure — caller should fall back to the original.
 */
export async function createThumbnail(file, options = {}) {
  if (!file) return null;
  const maxDim = options.maxDim || DEFAULT_MAX_DIM;
  const quality = options.quality || DEFAULT_QUALITY;

  const viaBitmap = await viaCreateImageBitmap(file, maxDim, quality);
  if (viaBitmap) return viaBitmap;

  return viaImageElement(file, maxDim, quality);
}

/**
 * Generate thumbnails for an array of files with bounded concurrency,
 * so 70+ photos don't all decode at once.
 */
export async function createThumbnails(files, options = {}) {
  const concurrency = options.concurrency || 3;
  const results = new Array(files.length).fill(null);
  let cursor = 0;

  async function worker() {
    while (cursor < files.length) {
      const i = cursor;
      cursor += 1;
      results[i] = await createThumbnail(files[i], options);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, files.length) },
    () => worker(),
  );
  await Promise.all(workers);
  return results;
}

export function revokeThumbnail(url) {
  if (typeof url === 'string' && url.startsWith('blob:')) {
    try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
  }
}

export function revokeThumbnails(urls) {
  if (!urls) return;
  urls.forEach(revokeThumbnail);
}
